/**
 * Soma planner adoption — the admin tab (Ed, 2026-09-22).
 *
 * "Which clients, and therefore which planners, haven't got going?" One row
 * per OneTouch care manager (= the planner), expandable to their clients,
 * each tagged with the first step it is stuck at. Read-only; everything
 * comes from omon-api `/v1/soma_adoption` (admin-only, ADR-0033 callable
 * wire protocol, same as the chase queue). The numbers are the hourly
 * summary's, so the page says when that summary was made.
 */
import { getFunctions, httpsCallableFromURL, FunctionsError } from "firebase/functions";
import { app } from "../firebase";
import {
  type AdoptionResponse,
  type ClientRow,
  type ManagerRow,
  managerLabel,
  ofLabel,
  pct,
  stageLabel,
  windowLabel,
} from "./model";

const OMON_API_ADOPTION_URL = "https://omon-api-myhmkw6jkq-nw.a.run.app/v1/soma_adoption";

const callable = httpsCallableFromURL<Record<string, unknown>, AdoptionResponse>(
  getFunctions(app, "europe-west2"),
  OMON_API_ADOPTION_URL,
);

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

function container(): HTMLElement | null {
  return document.getElementById("adoptionView");
}

function message(title: string, body = ""): void {
  const el = container();
  if (el) el.innerHTML = `<div class="empty-state"><h3>${esc(title)}</h3>${body ? `<p>${esc(body)}</p>` : ""}</div>`;
}

/** Panel ENTRY point (navigation.ts). */
export async function enterAdoption(): Promise<void> {
  message("Loading adoption…");
  try {
    paint((await callable({})).data);
  } catch (e) {
    message("Couldn't load adoption", e instanceof FunctionsError ? e.message : String(e));
  }
}

function bar(n: number, of: number): string {
  return `<span class="adopt-bar"><span style="width:${pct(n, of)}%"></span></span>`;
}

function clientRow(c: ClientRow): string {
  return `<tr>
    <td class="chase-client">${esc(c.name ?? c.eocId)}</td>
    <td><span class="adopt-stage ${esc(c.stage)}">${esc(stageLabel(c.stage))}</span></td>
    <td class="chase-soft">${c.shiftsDue ? `${ofLabel(c.shiftsEntered, c.shiftsDue)} shifts had something entered` : ""}</td>
  </tr>`;
}

function managerRow(m: ManagerRow): string {
  return `<tr><td colspan="5" style="padding:0">
    <details class="adopt-mgr">
      <summary><table class="chase-table" style="min-width:0;border:none"><tr>
        <td style="width:34%"><span class="adopt-caret">›</span><span class="chase-client">${esc(managerLabel(m))}</span></td>
        <td style="width:12%">${m.clients}</td>
        <td style="width:18%">${bar(m.withPlan, m.clients)}${ofLabel(m.withPlan, m.clients)}</td>
        <td style="width:14%">${m.withMandatory}</td>
        <td style="width:22%">${m.shiftsDue ? `${bar(m.shiftsEntered, m.shiftsDue)}${ofLabel(m.shiftsEntered, m.shiftsDue)}` : `<span class="chase-soft">none due</span>`}</td>
      </tr></table></summary>
      <table class="adopt-clients"><tbody>${m.clientRows.map(clientRow).join("")}</tbody></table>
    </details>
  </td></tr>`;
}

function paint(data: AdoptionResponse): void {
  const el = container();
  if (!el) return;
  if (!data.available) {
    message("No adoption summary yet",
      "It is written by the hourly chase job. It appears after the job's next run.");
    return;
  }
  const t = data.totals ?? {};
  const made = data.generatedAt
    ? new Date(data.generatedAt).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })
    : "unknown";
  el.innerHTML = `
    <div class="chase-head"><h2>Soma adoption</h2>
      <span class="chase-count">${esc(windowLabel(data.window))}</span>
      <span class="chase-soft">summary made ${esc(made)} · updates hourly</span></div>
    <p class="adopt-totals"><b>${t.withPlan ?? 0} of ${t.clients ?? 0}</b> clients have a plan ·
      <b>${t.withMandatory ?? 0}</b> with mandatory items · <b>${t.inUse ?? 0}</b> in use this week ·
      <b>${t.shiftsEntered ?? 0} of ${t.shiftsDue ?? 0}</b> shifts had something entered in the app</p>
    <div class="chase-table-wrap"><table class="chase-table">
      <thead><tr><th style="width:34%">Care manager</th><th style="width:12%">Clients</th>
        <th style="width:18%">Have a plan</th><th style="width:14%">Mandatory items</th>
        <th style="width:22%">Shifts entered this week</th></tr></thead>
      <tbody>${(data.managers ?? []).map(managerRow).join("")}</tbody>
    </table></div>
    <p class="chase-soft" style="margin-top:12px">Tap a care manager to see their clients and where each is stuck.
      Carers can only record against a care plan, so the plan columns come first.</p>`;
}
